import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { ReactionBar } from "./ReactionBar";
import { queryKeys } from "@/lib/queryKeys";
import { MarkdownView } from "./MarkdownView";
import { MentionTextarea } from "./MentionTextarea";
import { Avatar } from "./Avatar";
import { Button } from "./Button";

/**
 * CommentThread — designs/TaskDetail.html `.cmt` rows + `.composer`.
 *
 * Renders the comments list (avatar + author header + body) and a
 * sticky composer textarea at the bottom that posts a new comment.
 *
 * `meId` / `meName` are passed in from `<TaskDetail>` for the composer
 * avatar — keeps the `/api/me` query at the top of the tree.
 */

function formatCommentTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d
    .toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    .replace(" ", "");
  if (sameDay) return `TODAY ${time}`;
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  const isYest =
    d.getFullYear() === yest.getFullYear() &&
    d.getMonth() === yest.getMonth() &&
    d.getDate() === yest.getDate();
  if (isYest) return "YESTERDAY";
  return d
    .toLocaleDateString(undefined, { month: "short", day: "numeric" })
    .toUpperCase();
}

export function CommentThread({
  taskId,
  projectId,
  meId,
  meName,
}: {
  taskId: string;
  projectId: string;
  meId?: string;
  meName?: string;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  // Which top-level comment id is currently being replied to, + that draft.
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");
  // Collapsed thread ids (collapsed hides the replies but keeps the count).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.commentsOf(taskId),
    queryFn: () => api.listComments(taskId),
  });
  // Resolve author display names from the team list (fall back to the raw id
  // when not found — e.g. for ex-members whose row was removed).
  const team = useQuery({
    queryKey: queryKeys.team(),
    queryFn: api.listTeam,
  });
  const usersById = new Map(
    (team.data?.members ?? []).map((m) => [m.id, m.name || m.email]),
  );
  const imagesById = new Map(
    (team.data?.members ?? []).map((m) => [m.id, m.image ?? null]),
  );
  // Members offered by the @-mention autocomplete (workspace members).
  const mentionMembers = (team.data?.members ?? []).map((m) => ({
    id: m.id,
    name: m.name || m.email || m.id.slice(0, 8),
  }));
  const mut = useMutation({
    mutationFn: (v: { text: string; parentId?: string }) =>
      api.postComment(taskId, v.text, v.parentId),
    onSuccess: () => {
      // Posting a comment writes an activity_log row and is visible to other
      // members as an inbox event (the inbox relevance filter already targets
      // every prior commenter on the task, so a reply notifies the parent
      // author + thread participants). Refresh the comment list, activity
      // feed, and inbox badge so the action shows everywhere immediately.
      qc.invalidateQueries({ queryKey: queryKeys.commentsOf(taskId) });
      qc.invalidateQueries({ queryKey: queryKeys.activityFor(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.inboxUnread() });
      qc.invalidateQueries({ queryKey: queryKeys.inbox() });
      setBody("");
      setReplyBody("");
      setReplyTo(null);
    },
  });

  const allComments = data?.comments ?? [];
  const topLevel = allComments.filter((c) => !c.parentId);
  const repliesByParent = new Map<string, typeof allComments>();
  for (const c of allComments) {
    if (c.parentId) {
      const arr = repliesByParent.get(c.parentId) ?? [];
      arr.push(c);
      repliesByParent.set(c.parentId, arr);
    }
  }

  function renderComment(c: (typeof allComments)[number], isReply: boolean) {
    return (
      <div className={`cmt${isReply ? " cmt-reply" : ""}`} key={c.id}>
        <Avatar
          id={c.authorId}
          name={usersById.get(c.authorId) ?? c.authorId}
          size="md"
          image={imagesById.get(c.authorId)}
        />
        <div className="c-body">
          <div className="c-head">
            <Link
              to="/team/$userId"
              params={{ userId: c.authorId }}
              className="c-author"
            >
              <b>{usersById.get(c.authorId) ?? c.authorId}</b>
            </Link>
            <span className="ct">{formatCommentTimestamp(c.createdAt)}</span>
          </div>
          <div className="c-text">
            <MarkdownView source={c.body} />
          </div>
          <div className="c-actions">
            <ReactionBar targetType="comment" targetId={c.id} />
            {!isReply && (
              <button
                type="button"
                className="link-btn c-reply-btn"
                onClick={() => {
                  setReplyTo((r) => (r === c.id ? null : c.id));
                  setReplyBody("");
                }}
              >
                Reply
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div aria-label="Comments">
      {isLoading && <p className="t-sm t-muted">Loading comments…</p>}
      {isError && <p className="t-sm t-muted">Couldn't load comments.</p>}
      {data &&
        topLevel.map((c) => {
          const replies = repliesByParent.get(c.id) ?? [];
          const isCollapsed = collapsed.has(c.id);
          return (
            <div className="cmt-thread" key={c.id}>
              {renderComment(c, false)}
              {replies.length > 0 && (
                <button
                  type="button"
                  className="link-btn c-thread-toggle"
                  aria-expanded={!isCollapsed}
                  onClick={() =>
                    setCollapsed((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.id)) next.delete(c.id);
                      else next.add(c.id);
                      return next;
                    })
                  }
                >
                  {isCollapsed ? "▸" : "▾"} {replies.length}{" "}
                  {replies.length === 1 ? "reply" : "replies"}
                </button>
              )}
              {!isCollapsed && replies.map((r) => renderComment(r, true))}
              {replyTo === c.id && (
                <form
                  className="composer c-reply-composer"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const text = replyBody.trim();
                    if (text) mut.mutate({ text, parentId: c.id });
                  }}
                >
                  <div className="cwrap">
                    <textarea
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder="Write a reply…"
                      aria-label="Write a reply"
                      autoFocus
                    />
                    <div className="cbar">
                      <span className="sp" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplyTo(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={mut.isPending || replyBody.trim() === ""}
                      >
                        Reply
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          );
        })}
      {data && topLevel.length === 0 && (
        <p className="t-sm t-muted">Be the first to comment.</p>
      )}

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          const text = body.trim();
          if (text) mut.mutate({ text });
        }}
      >
        {meId ? (
          <Avatar
            id={meId}
            name={meName ?? "Me"}
            size="md"
            image={imagesById.get(meId)}
          />
        ) : (
          <div
            className="avatar avatar-md"
            style={{ background: "var(--subtle)", color: "var(--muted)" }}
            aria-hidden="true"
          >
            ?
          </div>
        )}
        <div className="cwrap">
          {/* @-mention picker: typing `@` surfaces a member list; selecting
              inserts a profile-linked token that renders as a chip and
              notifies the member. */}
          <MentionTextarea
            value={body}
            onChange={setBody}
            members={mentionMembers}
            placeholder="Write a comment… (@ to mention)"
            ariaLabel="Write a comment"
          />
          <div className="cbar">
            <span className="sp" />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={mut.isPending || body.trim() === ""}
            >
              Comment
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
